// Pre-create check (atomic-design Rule 3): considered `SpherePropertiesPanel` and
// `PerspectiveValueEditorMolecule`. `SpherePropertiesPanel` is a PrimeReact `Sidebar` around
// the chat panel — a container, not a property renderer. `PerspectiveValueEditorMolecule`
// edits one perspective's value and has no notion of a render MODE or of resolving which
// renderer a `SchemaProperty` should get. This dispatches on `resolveRenderer` and can land
// on either a design-system component or a composition host, which is a routing contract
// neither of those can absorb without becoming a different component.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SchemaProperty } from 'types/schema';
import type { RenderMode } from 'types/aspect';
import type { SpaceDocument } from 'types/space';
import { resolveRenderer, type ResolvedRenderer } from '../../../utils/resolveRenderer';
import { DesignSystemComponentRenderer } from '../../DesignSystemComponentRenderer';
import { getAdapter } from '../../../composition-host/adapterRegistry';
import CompositionHost from '../../../composition-host/CompositionHost';
import { useCompositionResolver } from '../../../composition-host/useCompositionResolver';
import type { AdapterContext, SpaceAction, SpaceHostMessage } from '../../../composition-host/types';
import './UnifiedPropertyRendererMolecule.scss';

// `getAdapter` ensures the built-in adapters are registered on first lookup — no eager
// module-scope call needed (see `adapterRegistry.ts`'s `ensureBuiltIns`).

const SURFACE_ID = 'unified-property-renderer';

export interface UnifiedPropertyRendererMoleculeProps {
  property: SchemaProperty;
  value: unknown;
  /** Absent in read-only view/compact/empty contexts. */
  onChange?: (value: unknown) => void;
  mode: RenderMode;
  /** Required only for a `components.properties.<name>.<mode>` ref whose `origin` is `'composition'`. */
  apiClient?: ApiClient;
  className?: string;
  /**
   * Caller-computed props merged OVER whatever the resolved ref supplies (a static
   * `RendererRef.props` for an explicit/type-default ref, or the property's own
   * `editComponentProps`/`displayComponentProps` for a legacy shim) — e.g. schema-rule-derived
   * `min`/`max`/`precision` a code-defined type default has no way to express statically
   * (EAINT-855 Phase 4).
   */
  extraProps?: Record<string, unknown>;
  /**
   * Forwarded to a legacy-shim component's `onEdit` (the dock-to-tab affordance
   * `AdditionalPropertiesGrid.handleEdit` implements) — meaningless for the catalog/composition
   * paths, which have no `onEdit` concept.
   */
  onEdit?: () => void;
  /**
   * Skips the internal `resolveRenderer` call and uses this instead — for a whole-aspect
   * renderer (`Schema.aspectComponents[aspectName]`), which resolves against an aspect-level
   * `RendererMap`, not a single property's chain (EAINT-855 spec §Resolution chain, whole aspect).
   */
  resolvedOverride?: ResolvedRenderer;
  /**
   * Fires once, when a `composition`-origin ref's fetch settles WITHOUT resolving a definition
   * (unpublished, deleted, 404, or a non-404 fetch error — `useCompositionResolver` collapses all
   * three to "unresolved" on purpose). Absent for every ordinary per-property mount, where the
   * generic unknown-component placeholder inside `CompositionHost` is the correct degradation;
   * `WholeAspectRenderer` is the one caller that wires this, to fall back to the aspect's flattened
   * property rows instead (EAINT-855 spec Edge Cases "never blank"; review pass 2, H-3).
   */
  onCompositionResolutionFailed?: () => void;
}

interface CatalogAdapterHostProps {
  catalogName: string;
  refProps?: Record<string, unknown>;
  extraProps?: Record<string, unknown>;
  value: unknown;
  onChange?: (value: unknown) => void;
  mode: RenderMode;
  property: SchemaProperty;
  className?: string;
}

/**
 * Mounts a `catalog`-origin `RendererRef` through the composition-host adapter registry, the same
 * adapters a Space surface uses. Adapters expect a `ctx` (`AdapterContext`) — this synthesizes the
 * minimal one a standalone property render needs: `resolveValue` is the identity (the value is
 * passed as a literal prop, never a `$bind` pointer), and `onAction` maps the adapter's `change`
 * action straight to `onChange` (EAINT-855 spec §Renderer contract).
 */
const CatalogAdapterHost: React.FC<CatalogAdapterHostProps> = ({
  catalogName,
  refProps,
  extraProps,
  value,
  onChange,
  mode,
  property,
  className,
}) => {
  const ctx: AdapterContext = useMemo(
    () => ({
      renderChild: () => null,
      renderChildren: () => [],
      renderChildInScope: () => null,
      getComponentEntry: () => undefined,
      resolveValue: (bound: unknown) => bound,
      onAction: (action: SpaceAction) => {
        if (action.name === 'change') onChange?.(action.context?.value);
      },
      surfaceId: SURFACE_ID,
      mode: 'live',
      env: {},
      dataModel: {},
    }),
    [onChange]
  );

  const registeredAdapter = getAdapter(catalogName);
  if (!registeredAdapter) {
    // eslint-disable-next-line no-console
    console.warn(
      `[UnifiedPropertyRendererMolecule] no adapter registered for catalog type "${catalogName}" — falling through to the terminal default`,
      { property: property.name, mode }
    );
  }
  const Adapter = registeredAdapter ?? getAdapter(mode === 'edit' ? 'TextField' : 'GenericDisplay');
  if (!Adapter) return null; // unreachable — TextField/GenericDisplay are always registered above

  return (
    <div className={['unified-property-renderer__adapter', className].filter(Boolean).join(' ')} data-mode={mode}>
      <Adapter
        componentId={property.name}
        props={{
          value,
          content: value,
          onValueChanged: mode === 'edit' ? 'change' : undefined,
          disabled: mode !== 'edit',
          // Name for adapters that render the control with no visible label of their own.
          ariaLabel: property.label || property.name,
          ...refProps,
          ...extraProps,
          // Host-owned mode invariant, re-asserted AFTER the spreads: an authored
          // `RendererRef.props.disabled: false` must not make a read-only control look
          // interactive. Only pinned outside edit mode — in edit mode a definition is free to
          // disable its own control.
          ...(mode !== 'edit' ? { disabled: true } : {}),
        }}
        ctx={ctx}
      />
    </div>
  );
};

interface CompositionRendererBridgeProps {
  refName: string;
  refVersion?: number;
  refProps?: Record<string, unknown>;
  value: unknown;
  mode: RenderMode;
  property: SchemaProperty;
  onChange?: (value: unknown) => void;
  apiClient?: ApiClient;
  className?: string;
  onResolutionFailed?: () => void;
}

/**
 * Mounts a `composition`-origin `RendererRef`. Fetches + resolves via the same
 * `useCompositionResolver` every other composition host in this package uses, then reuses
 * `CompositionHost` to expand it (`expandComposition` binds `{value, mode, property, ...ref.props}`
 * as the composition's caller props, via `{ $bind: '/props/value' }` etc. inside the stored
 * definition). Change flows out via `CompositionHost.onAction` filtered on the composition's
 * declared `change` emit — NOT `onWireDelivery`, which only carries deliveries INTO a node
 * (EAINT-855 spec §Renderer contract, ratified planning decision).
 */
const CompositionRendererBridge: React.FC<CompositionRendererBridgeProps> = ({
  refName,
  refVersion,
  refProps,
  value,
  mode,
  property,
  onChange,
  apiClient,
  className,
  onResolutionFailed,
}) => {
  const { t } = useTranslation();

  const document: SpaceDocument = useMemo(
    () => ({
      formatVersion: 'space/v1',
      catalogVersion: 1,
      surface: { family: 'UnifiedPropertyRendererMolecule', componentId: 'root', layout: 'primary' },
      a2ui: {
        version: 'v1.0',
        catalogId: SURFACE_ID,
        components: [{ id: 'root', component: refName, componentVersion: refVersion }],
        root: 'root',
      },
    }),
    [refName, refVersion]
  );

  // No production call site passes `apiClient` — the grid, the field-input molecule, the
  // whole-aspect host and the docked tabs all mount this without one. `useCompositionResolver`
  // gates its fetch on `Boolean(apiClient)`, so defaulting from context is what makes
  // `origin: 'composition'` renderers resolve outside tests at all (SC1/SC2 composition half).
  const apiClients = useApiClientsOrNull();
  const effectiveApiClient = apiClient ?? apiClients?.schemaClient ?? undefined;

  const { resolveComposition, isResolving } = useCompositionResolver(document, effectiveApiClient);

  // The one signal `onResolutionFailed` exists for: the fetch has SETTLED (not `isResolving`) and
  // still has no definition for this exact ref — unpublished, deleted, 404, or a non-404 fetch
  // error, which `useCompositionResolver` already collapses to "unresolved" for us. Effect (not
  // inline) so it fires once per settle rather than on every render, and so it never fires while
  // still loading (that would mount the caller's fallback and this bridge's own loading span at
  // the same time).
  useEffect(() => {
    if (!onResolutionFailed || isResolving) return;
    const resolvedDefinition = resolveComposition?.({ systemName: refName, version: refVersion });
    if (!resolvedDefinition) onResolutionFailed();
  }, [onResolutionFailed, isResolving, resolveComposition, refName, refVersion]);

  const messages: SpaceHostMessage[] = useMemo(
    () => [
      {
        type: 'createSurface',
        surfaceId: SURFACE_ID,
        components: [
          {
            id: 'root',
            component: refName,
            componentVersion: refVersion,
            value,
            mode,
            property,
            ...refProps,
          },
        ],
        root: 'root',
      },
    ],
    [refName, refVersion, value, mode, property, refProps]
  );

  const handleAction = (action: SpaceAction) => {
    if (action.name === 'change') onChange?.(action.context?.value);
  };

  if (isResolving) {
    return (
      <span className={['unified-property-renderer__loading', className].filter(Boolean).join(' ')}>
        {t('space.render.resolvingCompositions')}
      </span>
    );
  }

  return (
    <div className={['unified-property-renderer__composition', className].filter(Boolean).join(' ')} data-mode={mode}>
      <CompositionHost
        messages={messages}
        surfaceId={SURFACE_ID}
        mode="live"
        resolveComposition={resolveComposition}
        onAction={handleAction}
      />
    </div>
  );
};

/**
 * Hydrates a `reference` property's stored sphere id into the sphere object that `SphereCard`
 * (the `reference` view type default) declares as its `sphere` prop, then mounts the adapter.
 *
 * The fetch lives HERE, in the host, and not in the adapter on purpose: `sphereDomainAdapters`
 * documents a declarative-only invariant — a2ui/space adapters render already-resolved data-model
 * values and never reach out on their own. `UnifiedPropertyRendererMolecule` is a trusted host (mounted by
 * platform code against a schema property, never by an authored document), so resolving the id is
 * its responsibility. Reads go through `globalSphereCache` per /standards/frontend/sphere-avatar.md,
 * which batches and caches; a failed or unresolvable id degrades to the raw id rather than blanking.
 */
const ReferenceValueHost: React.FC<CatalogAdapterHostProps> = (props) => {
  const { value, extraProps } = props;
  const apiClients = useApiClientsOrNull();
  const sphereId = typeof value === 'string' && value ? value : undefined;
  const [sphere, setSphere] = useState<{ name?: string; status?: string } | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!sphereId || !apiClients?.graphClient) {
      setSphere(undefined);
      return () => {
        cancelled = true;
      };
    }
    globalSphereCache
      .getSphereData(sphereId, apiClients.graphClient)
      .then((data) => {
        if (cancelled) return;
        const properties = data?.info?.sphere?.properties as Record<string, unknown> | undefined;
        setSphere({
          name: properties?.name as string | undefined,
          status: properties?.status as string | undefined,
        });
      })
      .catch(() => {
        if (!cancelled) setSphere(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [sphereId, apiClients?.graphClient]);

  // Until (or unless) the id resolves, show the id itself — never a blank card.
  const resolvedSphere = sphere?.name ? sphere : { name: sphereId, status: sphere?.status };
  return <CatalogAdapterHost {...props} extraProps={{ ...extraProps, sphere: resolvedSphere }} />;
};

/**
 * The one render entry point for aspect/property data (EAINT-855 spec). Runs `resolveRenderer`'s
 * chain and mounts whatever it resolves to: a catalog adapter, a composition, or (for legacy
 * `editComponent`/`displayComponent`/`component` refs carrying a `valueAccessor`/`changeMapper`)
 * `DesignSystemComponentRenderer` itself — the multi-field fan-out semantics it implements are not
 * yet reproduced natively, so the shim reuses it internally rather than re-deriving them (gap 5).
 */
const UnifiedPropertyRendererMolecule: React.FC<UnifiedPropertyRendererMoleculeProps> = ({
  property,
  value,
  onChange,
  mode,
  apiClient,
  className,
  extraProps,
  onEdit,
  resolvedOverride,
  onCompositionResolutionFailed,
}) => {
  const { t } = useTranslation();
  const resolved = useMemo(
    () => resolvedOverride ?? resolveRenderer(property, mode),
    [resolvedOverride, property, mode]
  );

  if (resolved.isEmptyAffordance) {
    return (
      <span className={['unified-property-renderer__empty', className].filter(Boolean).join(' ')}>
        {t('space.render.noValue')}
      </span>
    );
  }

  const ref = resolved.ref;
  if (!ref) return null; // unreachable — resolveRenderer only omits `ref` when `isEmptyAffordance` is true

  if (ref.origin === 'composition') {
    return (
      <CompositionRendererBridge
        refName={ref.name}
        refVersion={ref.version}
        refProps={ref.props}
        value={value}
        mode={mode}
        property={property}
        onChange={onChange}
        apiClient={apiClient}
        className={className}
        onResolutionFailed={onCompositionResolutionFailed}
      />
    );
  }

  if (resolved.legacyShim) {
    return (
      <DesignSystemComponentRenderer
        componentName={ref.name}
        value={value}
        onChange={onChange ?? (() => undefined)}
        onEdit={onEdit}
        componentProps={{ ...resolved.legacyShim.componentProps, ...extraProps }}
        valueAccessor={resolved.legacyShim.valueAccessor}
        changeMapper={resolved.legacyShim.changeMapper}
      />
    );
  }

  // `SphereCard` declares a `sphere` OBJECT prop, not the bare `value` the rest of the type-default
  // table takes — a stored reference id must be hydrated before it can render as a card.
  const Host = ref.name === 'SphereCard' ? ReferenceValueHost : CatalogAdapterHost;

  return (
    <Host
      catalogName={ref.name}
      refProps={ref.props}
      extraProps={extraProps}
      value={value}
      onChange={onChange}
      mode={mode}
      property={property}
      className={className}
    />
  );
};

export default UnifiedPropertyRendererMolecule;
